import { useHistory } from 'react-router';
import { Tooltip, Button, svgIconClasses, buttonClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowRightSLineIcon } from '../RemixIcons/generated/remixIcons';

const SidebarButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'nested' && prop !== 'depth' && prop !== 'showActiveMarker',
})(({ active, nested, depth = 0, showActiveMarker = true, theme }) => {
  const nestedIndent = nested ? 66 + Math.max(0, depth - 1) * 16 : 0;

  return {
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'normal',
  boxSizing: 'border-box',
  color: '#FFFFFF',
  height: '45px',
  width: '100%',
  minWidth: '0',
  borderRadius: '0',
  padding: '0',
  paddingLeft: nested ? `${nestedIndent}px` : '0',
  justifyContent: 'flex-start',
  textAlign: 'left',
  border: '1px solid transparent',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",

  '&&:hover': {
    color: '#FFFFFF',
    backgroundColor: '#16171C',
  },
  '&&:active': {
    backgroundColor: '#16171C',
  },
  [`&.${buttonClasses.focusVisible}`]: {
    outline: 'none',
    boxShadow: 'none',
    backgroundColor: '#16171C',
    border: `1px solid ${theme.palette.common.white}`,
  },
  [`& .${buttonClasses.startIcon}`]: {
    marginLeft: '0',
    width: '58px',
    height: '58px',
    display: 'flex',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'center',
    [`& .${svgIconClasses.root}`]: {
      fontSize: '24px',
    },
  },
  [`& .${buttonClasses.endIcon}`]: {
    marginLeft: 'auto',
    marginRight: '16px',
    [`& .${svgIconClasses.root}`]: {
      fontSize: '20px',
      transition: 'transform 0.2s ease',
    },
  },
  backgroundColor: active ? '#16171C' : undefined,
  '&::before': active && showActiveMarker
    ? {
        content: '""',
        position: 'absolute',
        left: '4px',
        width: '4px',
        height: '16px',
        backgroundColor: '#9d64f0',
        borderRadius: '4px',
        boxShadow: '0px 0px 6px #9d64f0',
      }
    : undefined,
  };
});

export const RouteButton = ({ title, expanded, isActive, onClick, path, params = '', depth = 0, ...rest }) => {
  const history = useHistory();
  return (
    <Tooltip
      placement="right"
      title={title}
      arrow
      disableFocusListener={expanded}
      disableHoverListener={expanded}
      disableTouchListener={expanded}
    >
      <SidebarButton
        onClick={() => {
          const destination = `${path}${params || ''}`;
          const current = `${history.location.pathname}${history.location.search || ''}`;
          if (current === destination) {
            window.dispatchEvent(new CustomEvent('playground:same-route-click', {
              detail: {
                path: history.location.pathname,
                destination,
              },
            }));
            return;
          }
          onClick(destination);
        }}
        active={isActive}
        depth={depth}
        {...rest}
      />
    </Tooltip>
  );
};

// A non-navigating sidebar button used as the header of a group of nested
// children. Visually identical to RouteButton, with an end-aligned chevron
// indicating whether the group is open. The chevron is hidden visually when
// the sidebar is collapsed (parent's `overflow: hidden` clips it).
export const GroupButton = ({ title, expanded, isOpen, isActive, onToggle, startIcon, children, depth = 0, ...rest }) => (
  <Tooltip
    placement="right"
    title={title}
    arrow
    disableFocusListener={expanded}
    disableHoverListener={expanded}
    disableTouchListener={expanded}
  >
    <SidebarButton
      onClick={onToggle}
      active={isActive}
      nested={depth > 0}
      depth={depth}
      showActiveMarker={rest.showActiveMarker}
      startIcon={startIcon}
      endIcon={
        expanded ? (
          <ArrowRightSLineIcon
            style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
          />
        ) : null
      }
      aria-expanded={isOpen}
      {...rest}
    >
      {children}
    </SidebarButton>
  </Tooltip>
);
